/**
 * Cross-origin client for Company's auth endpoints.
 *
 * Two flows:
 *   1. First login — `loginViaCompany({email, password})` posts to authifier's
 *      `/auth/session/login`, then exchanges the returned session token for an
 *      EarthSocial-scoped JWT via Company's `/auth/external-token`. Company sets
 *      a parent-domain `company_session` cookie as a side effect.
 *   2. Silent refresh — `refreshJwt()` calls `/auth/external-token` with
 *      `credentials: 'include'`. The parent-domain cookie travels automatically;
 *      a fresh JWT comes back. Used by AuthContext on a 30s-before-expiry timer
 *      and by fetchWithAuth on 401.
 *
 * MFA is not currently supported via the embedded widget. If authifier returns
 * `result: "MFA"`, the caller surfaces an error and tells the user to log in on
 * Company directly first.
 */

const COMPANY_BASE_URL = import.meta.env.VITE_COMPANY_BASE_URL || 'https://api.company.earthservers.net';
const AUDIENCE = 'earthsocial';

export interface CompanyJwt {
  jwt: string;
  expiresAt: string; // ISO-8601
  companyUserId: string;
}

interface AuthifierSuccess {
  result: 'Success';
  _id: string;
  user_id: string;
  token: string;
  name: string;
  last_seen: string;
}

interface AuthifierMfa {
  result: 'MFA';
  ticket: string;
  allowed_methods: string[];
}

interface AuthifierDisabled {
  result: 'Disabled';
  user_id: string;
}

type AuthifierLoginResponse = AuthifierSuccess | AuthifierMfa | AuthifierDisabled;

export class CompanyAuthError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

async function exchangeSessionForJwt(sessionToken: string): Promise<CompanyJwt> {
  const res = await fetch(`${COMPANY_BASE_URL}/auth/external-token`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify({ audience: AUDIENCE }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new CompanyAuthError(body.error ?? 'exchange_failed', `external-token returned ${res.status}`);
  }
  return (await res.json()) as CompanyJwt;
}

export async function loginViaCompany(email: string, password: string): Promise<CompanyJwt> {
  const res = await fetch(`${COMPANY_BASE_URL}/auth/session/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, friendly_name: 'EarthSocial' }),
  });
  if (!res.ok) {
    throw new CompanyAuthError('invalid_credentials', 'Invalid email or password');
  }
  const data = (await res.json()) as AuthifierLoginResponse;
  if (data.result === 'MFA') {
    throw new CompanyAuthError(
      'mfa_required',
      'This account requires multi-factor auth. Please log in at company.earthservers.net first, then return here.',
    );
  }
  if (data.result === 'Disabled') {
    throw new CompanyAuthError('account_disabled', 'This account has been disabled.');
  }
  return exchangeSessionForJwt(data.token);
}

/**
 * Refresh the EarthSocial JWT using the parent-domain `company_session` cookie.
 * Returns null when no valid session cookie exists — caller should bounce the
 * user to the login screen.
 */
export async function refreshJwt(): Promise<CompanyJwt | null> {
  const res = await fetch(`${COMPANY_BASE_URL}/auth/external-token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ audience: AUDIENCE }),
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new CompanyAuthError(body.error ?? 'refresh_failed', `external-token returned ${res.status}`);
  }
  return (await res.json()) as CompanyJwt;
}

/** Best-effort logout. Clears Company's session + the parent-domain cookie. */
export async function logoutCompany(): Promise<void> {
  await fetch(`${COMPANY_BASE_URL}/auth/session/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => {
    // Network errors on logout don't matter — local state will clear regardless.
  });
}

export const COMPANY_SIGNUP_URL =
  import.meta.env.VITE_COMPANY_SIGNUP_URL || `${COMPANY_BASE_URL}/signup`;
